import type { StateCreator } from 'zustand';
import type { Settings, Locale } from '@/domain/settings/types';
import { DEFAULT_SETTINGS, clampVolume, clampFontScale } from '@/domain/settings/types';
import type { AppStore } from './types';

export interface SettingsSliceState {
  settings: Settings;
}

export interface SettingsSliceActions {
  setLocale: (locale: Locale) => void;
  setMasterVolume: (v: number) => void;
  setSfxVolume: (v: number) => void;
  setAmbientVolume: (v: number) => void;
  setMusicVolume: (v: number) => void;
  setMuted: (muted: boolean) => void;
  setFontScale: (v: number) => void;
  setReducedMotion: (v: boolean) => void;
  setCloudEnabled: (v: boolean) => void;
  setFastMode: (v: boolean) => void;
  setOnboarded: (v: boolean) => void;
  setColorblindMode: (v: 'off' | 'deutero') => void;
  hydrateSettings: (s: Partial<Settings>) => void;
  resetSettings: () => void;
}

export type SettingsSlice = SettingsSliceState & SettingsSliceActions;

export const createSettingsSlice: StateCreator<AppStore, [], [], SettingsSlice> = (set) => ({
  settings: DEFAULT_SETTINGS,
  setLocale: (locale) => {
    set((s) => ({ settings: { ...s.settings, locale } }));
  },
  setMasterVolume: (v) => {
    set((s) => ({ settings: { ...s.settings, audio: { ...s.settings.audio, masterVolume: clampVolume(v) } } }));
  },
  setSfxVolume: (v) => {
    set((s) => ({ settings: { ...s.settings, audio: { ...s.settings.audio, sfxVolume: clampVolume(v) } } }));
  },
  setAmbientVolume: (v) => {
    set((s) => ({ settings: { ...s.settings, audio: { ...s.settings.audio, ambientVolume: clampVolume(v) } } }));
  },
  setMusicVolume: (v) => {
    set((s) => ({ settings: { ...s.settings, audio: { ...s.settings.audio, musicVolume: clampVolume(v) } } }));
  },
  setMuted: (muted) => {
    set((s) => ({ settings: { ...s.settings, audio: { ...s.settings.audio, muted } } }));
  },
  setFontScale: (v) => {
    set((s) => ({
      settings: { ...s.settings, accessibility: { ...s.settings.accessibility, fontScale: clampFontScale(v) } },
    }));
  },
  setReducedMotion: (v) => {
    set((s) => ({
      settings: { ...s.settings, accessibility: { ...s.settings.accessibility, reducedMotion: v } },
    }));
  },
  setCloudEnabled: (enabled) => {
    set((s) => ({ settings: { ...s.settings, cloud: { ...s.settings.cloud, enabled } } }));
  },
  setFastMode: (fastMode) => {
    set((s) => ({ settings: { ...s.settings, fastMode } }));
  },
  setOnboarded: (onboarded) => {
    set((s) => ({ settings: { ...s.settings, onboarded } }));
  },
  setColorblindMode: (colorblindMode) => {
    set((s) => ({
      settings: {
        ...s.settings,
        accessibility: { ...s.settings.accessibility, colorblindMode },
      },
    }));
  },
  hydrateSettings: (partial) => {
    set((s) => ({
      settings: {
        ...s.settings,
        ...partial,
        audio: { ...s.settings.audio, ...(partial.audio ?? {}) },
        accessibility: { ...s.settings.accessibility, ...(partial.accessibility ?? {}) },
        cloud: { ...s.settings.cloud, ...(partial.cloud ?? {}) },
      },
    }));
  },
  resetSettings: () => {
    set({ settings: DEFAULT_SETTINGS });
  },
});
